import {
  getValidMatchingOptions,
  isAnswerComplete,
  scoreMatching
} from "@pathwright/blocks-core/src/blocks/question/type/completion"
import {
  BLOCK_INPUT_STATES,
  useBlockCompletion,
  useBlocksConfig
} from "@pathwright/blocks-engine"
import { BlockText } from "@pathwright/blocks/src/shared"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import findIndex from "lodash/findIndex"
import get from "lodash/get"
import React, { useEffect, useState } from "react"
import Select from "react-select"
import { isEmptyHTML, isMobile } from "../../../utils"
import { ExplanationFeedback } from "../MultipleChoice/view"
import { FeedbackIndicator } from "../ui"
import {
  INCORRECT,
  getAnswerMatches,
  getMatchingExplanationFeedback,
  getTextFromHTML,
  matchesToSelectOptions,
  normalizeMatchingOrdering,
  randomizeMatchesWithUserID
} from "../utils"
import { MatchingViewer, MatchingViewerItemWrap } from "./ui"

const MatchingQuestionViewer = (props) => {
  const { data, onChange, blockStyles, layout } = props

  const { t } = useTranslate()

  const [options, setOptions] = useState([])
  const [matches, setMatches] = useState([])
  const [answerMatches, setAnswerMatches] = useState([])

  const { hideAssessmentFeedback, disableInput, userID, templateVariables } =
    useBlocksConfig()
  const { setBlockFunctions } = useBlockCompletion()

  useEffect(() => {
    setBlockFunctions({
      validateInput,
      getBlockIsCompleted: (nextData) => isAnswerComplete(nextData, layout),
      scoreData: (data) => scoreMatching(data)
    })

    initializeMatching()
  }, [])

  const validateInput = (data) => {
    const answerMatches = getAnswerMatches(data)
    // console.log("answerMatches", answerMatches, data)
    if (!answerMatches.length) return BLOCK_INPUT_STATES.NONE

    if (
      answerMatches.length <
      getValidMatchingOptions(data.options, data.matches).length
    )
      return BLOCK_INPUT_STATES.INVALID

    return BLOCK_INPUT_STATES.VALID
  }

  const initializeMatching = () => {
    // Make sure options and matches are ordered correctly
    const { options: orderedOptions, matches: orderedMatches } =
      normalizeMatchingOrdering(data.options, data.matches)

    // 1. Options (left side)
    const options = getValidMatchingOptions(orderedOptions, orderedMatches)
    setOptions(options)

    // 2. Matches (right side). Randomize order for learner.
    const matches = [...orderedMatches].sort(randomizeMatchesWithUserID(userID))
    setMatches(matches)

    // 3. Get matches already selected by learner
    const answerMatches = getAnswerMatches(data)
    setAnswerMatches(answerMatches)
  }

  const onSelectMatch =
    (option) =>
    ({ label, value }) => {
      // Check if there's already a match for this option
      const selectionIndex = findIndex(answerMatches, { order: option.order })

      // Temp value: support legacy data with option/matches that have no value.
      const nextValue =
        !option.value || !value || value.includes("temp-")
          ? null
          : `${option.value}:${value}`

      const nextMatch = {
        label,
        value: nextValue,
        order: option.order
      }
      const nextMatches = [...answerMatches]

      // If match for this option already exists, replace it
      if (selectionIndex >= 0) {
        nextMatches[selectionIndex] = nextMatch
        // otherwise, add it
      } else {
        nextMatches.push(nextMatch)
      }

      setAnswerMatches(nextMatches)

      onChange({
        answer: {
          ...data.answer,
          selectedOptions: nextMatches
        }
      })
    }

  const answerMatchesAsSelectOptions = answerMatches.map((m) => ({
    ...m,
    label: getTextFromHTML(m.label)
  }))

  const isComplete = isAnswerComplete(data, layout)

  const showFeedback = !hideAssessmentFeedback && isComplete
  const selectableMatches = matchesToSelectOptions(matches)

  return (
    <React.Fragment>
      <MatchingViewer textColor={get(blockStyles, "styleAttributes.color")}>
        {options
          .filter((o) => !isEmptyHTML(o.label))
          .map((option) => {
            // This is the match that to the right of this option.
            // Matches and options are guaranteered to share the same order.
            const correspondingMatch = matches.find(
              (m) => m.order === option.order
            )

            // For newer scoring, the answer match's value will contain the option's value (`${o.value}:${m.value}`)
            // For legacy scoring, the answer match must be found by order
            let selectedMatch = answerMatchesAsSelectOptions?.find(
              (m) =>
                !!m.value && !!option.value && m.value.includes(option.value)
            )
            if (!selectedMatch) {
              selectedMatch = answerMatchesAsSelectOptions?.find(
                (m) => m.order === option.order
              )
            }

            const isCorrect =
              selectedMatch?.value ===
                `${option.value}:${correspondingMatch.value}` ||
              (getTextFromHTML(correspondingMatch.label) ===
                getTextFromHTML(selectedMatch?.label) &&
                selectedMatch?.order === option.order)

            const explanationFeedback = isCorrect
              ? ""
              : getMatchingExplanationFeedback(option, matches, t)

            // The match shown in the dropdown should be the one that
            // matches the selected match's value or label (legacy)
            const valueMatch = selectableMatches.find(
              (m) =>
                (!!m.value &&
                  !!selectedMatch?.value &&
                  selectedMatch?.value?.includes(m.value)) ||
                getTextFromHTML(m.label) ===
                  getTextFromHTML(selectedMatch?.label)
            )

            return (
              <MatchingViewerItemWrap
                key={`${option.value}:${correspondingMatch.value}`}
                showFeedback={showFeedback}
                isCorrect={isCorrect}
              >
                {showFeedback && !isCorrect && (
                  <FeedbackIndicator optionFeedback={INCORRECT} />
                )}
                <div>
                  <BlockText
                    html={option.label}
                    templateContext={templateVariables}
                    style={{ paddingRight: "1rem" }}
                  />
                  {showFeedback && explanationFeedback && (
                    <ExplanationFeedback label={explanationFeedback} />
                  )}
                </div>
                <Select
                  onChange={onSelectMatch(option)}
                  value={valueMatch}
                  options={selectableMatches}
                  styles={{
                    control: (baseStyles, state) => {
                      return {
                        ...baseStyles,
                        width: isMobile() ? "90px" : "180px",
                        minWidth: isMobile() ? "90px" : "180px",
                        border: `1px solid ${
                          state.isFocused ? "#6a9fcb !important" : "#ddd"
                        }`,
                        cursor: "pointer"
                      }
                    },
                    indicatorSeparator: (baseStyles) => ({
                      ...baseStyles,
                      display: "none"
                    }),
                    placeholder: (baseStyles) => ({
                      ...baseStyles,
                      color: "#aaa"
                    }),
                    option: (baseStyles, state) => {
                      return {
                        ...baseStyles,
                        backgroundColor: state.isSelected ? "#eee" : "#fff",
                        color: "#333",
                        cursor: "pointer"
                      }
                    }
                  }}
                  multi={false}
                  styleType="blank"
                  isDisabled={disableInput || isComplete}
                />
              </MatchingViewerItemWrap>
            )
          })}
      </MatchingViewer>
    </React.Fragment>
  )
}

export default MatchingQuestionViewer
